import { apiRequest, APIError } from '../utils/api';

/**
 * Get unread notification count for current user
 * @param {number} userId - User ID
 * @returns {Promise<number>} - Unread count
 */
export const getUnreadNotificationCount = async (userId) => {
  try {
    const res = await apiRequest(`/api/v1/notifications/unread-count/${userId}`);
    return res?.count ?? 0;
  } catch {
    return 0;
  }
};

/**
 * Get all notifications for current user
 * @param {number} userId - User ID
 * @returns {Promise<Array>} - Array of notifications
 */
export const getNotifications = async (userId) => {
  try {
    return await apiRequest(`/api/v1/notifications/${userId}`);
  } catch (error) {
    if (error instanceof APIError && error.code === 'NETWORK_ERROR') {
      error.message = 'Unable to load notifications. Please check your connection.';
    }
    throw error;
  }
};

export const markNotificationAsRead = async (userId, notificationId) => {
  try {
    return await apiRequest(
      `/api/v1/notifications/user/${userId}/notification/${notificationId}/read`,
      { method: 'PATCH' },
    );
  } catch (error) {
    console.warn('Failed to mark notification as read:', error);
    return { success: false };
  }
};

export const markAllNotificationsAsRead = async (userId) => {
  try {
    return await apiRequest(`/api/v1/notifications/${userId}/read-all`, {
      method: 'PATCH',
    });
  } catch (error) {
    console.warn('Failed to mark all notifications as read:', error);
    return { success: false };
  }
};

export const deleteNotification = async (userId, notificationId) => {
  return apiRequest(
    `/api/v1/notifications/user/${userId}/notification/${notificationId}`,
    { method: 'DELETE' },
  );
};

export const deleteNotifications = async (userId, notificationIds) => {
  if (!notificationIds?.length) return;
  await Promise.all(notificationIds.map((id) => deleteNotification(userId, id)));
};

export const deleteAllNonSystemNotifications = async (userId) => {
  return apiRequest(`/api/v1/notifications/user/${userId}/non-system`, {
    method: 'DELETE',
  });
};

/** Notify listeners (e.g. Navbar) that notification read state changed. */
export const notifyNotificationsUpdated = () => {
  window.dispatchEvent(new CustomEvent('notifications-updated'));
};
